import React from "react";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import * as jsondiffpatch from "jsondiffpatch";
import * as htmlFormatter from "jsondiffpatch/formatters/html";
import "jsondiffpatch/formatters/styles/html.css";

// The two views of a balancing doc: the value in a real JSON tool, and the difference between two versions of it
// as a tree (jsondiffpatch's html formatter - additions, removals, changes and array moves in place, nested as
// deep as the data goes). One category at a time by default; "All" is the whole doc (~1 MB), which the editor
// copes with because it renders what is expanded.

// vanilla-jsoneditor is the engine of jsoneditoronline.org (svelte-jsoneditor, ISC): tree, text and table modes,
// search, the path bar, copy. Read-only here - the docs are versioned on the backend, the sheets are the source.
// Loaded on first use: it is a CodeMirror + Ajv sized chunk the rest of the site never needs.
const MODE_KEY = "inspector.jsonMode";
// Table mode only opens arrays: an object category falls back to the tree instead of the editor's "cannot be
// opened in table mode" notice
const savedMode = (data) => {
    let mode = "tree";
    try { mode = localStorage.getItem(MODE_KEY) || "tree"; } catch { /* private window */ }
    return mode === "table" && !Array.isArray(data) ? "tree" : mode;
};
const saveMode = (mode) => { try { localStorage.setItem(MODE_KEY, mode); } catch { /* private window */ } };

export class JsonTree extends React.Component {
    state = { loading: true, error: null };
    container = React.createRef();
    editor = null;
    mounted = false;

    async componentDidMount() {
        this.mounted = true;
        try {
            const { createJSONEditor } = await import("vanilla-jsoneditor");
            if (!this.mounted || !this.container.current) return;
            this.editor = createJSONEditor({
                target: this.container.current,
                props: {
                    content: { json: this.props.data },
                    readOnly: true,
                    mode: savedMode(this.props.data),
                    mainMenuBar: true,
                    navigationBar: true,
                    statusBar: true,
                    indentation: 2,
                    askToFormat: false,
                    onChangeMode: saveMode,
                },
            });
            this.setState({ loading: false });
        } catch (e) {
            if (this.mounted) this.setState({ loading: false, error: e.message });
        }
    }

    componentDidUpdate(prev) {
        if (!this.editor || prev.data === this.props.data) return;
        this.editor.update({ json: this.props.data });
        this.editor.updateProps({ mode: savedMode(this.props.data) });
    }

    componentWillUnmount() {
        this.mounted = false;
        this.editor?.destroy();
        this.editor = null;
    }

    render() {
        const { data } = this.props;
        if (data === undefined) return <Typography variant="body2" color="text.secondary">Nothing here.</Typography>;
        if (this.state.error) return <Typography variant="body2" color="error">Could not load the JSON editor: {this.state.error}</Typography>;
        return (
            <Box>
                {this.state.loading && <Typography variant="body2" sx={{ mb: 1 }}>Loading the editor…</Typography>}
                <Box ref={this.container}
                     sx={{ height: "calc(100vh - 330px)", minHeight: 420, "--jse-font-family-mono": "ui-monospace, Menlo, monospace", "--jse-font-size-mono": "13px" }} />
            </Box>
        );
    }
}

// Rows of a list are matched by their id when they have one (a re-ordered list diffs as moves), and by position
// otherwise (a wave's monsters have no id: matching them by content would show an edit as a removal plus an addition)
const differ = jsondiffpatch.create({
    objectHash: (o, index) => o?.id ?? o?.missionId ?? `$$index:${index}`,
    arrays: { detectMove: true, includeValueOnMove: false },
});

export class JsonDiff extends React.Component {
    state = { showUnchanged: false };
    container = React.createRef();

    componentDidMount() { this.apply(); }
    componentDidUpdate() { this.apply(); }

    apply() {
        if (this.container.current) htmlFormatter.showUnchanged(this.state.showUnchanged, this.container.current, 0);
    }

    render() {
        const { left, right } = this.props;
        let html = null;
        try {
            const delta = differ.diff(left, right);
            html = delta ? htmlFormatter.format(delta, left) : null;
        } catch (e) {
            return <Typography variant="body2" color="error">Could not diff: {e.message}</Typography>;
        }
        if (!html) return <Typography variant="body2">Versions have the same content. No changes detected.</Typography>;
        return (
            <Box>
                <FormControlLabel
                    control={<Checkbox size="small" checked={this.state.showUnchanged} onChange={e => this.setState({ showUnchanged: e.target.checked })} />}
                    label={<Typography variant="body2">Show unchanged</Typography>}
                />
                <Box ref={this.container} sx={{ fontSize: 13, overflowX: "auto", "& .jsondiffpatch-delta": { fontFamily: "ui-monospace, Menlo, monospace" } }}
                     dangerouslySetInnerHTML={{ __html: html }} />
            </Box>
        );
    }
}
